import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import CssBaseline from '@mui/material/CssBaseline';
import {
	BrowserRouter as Router,
	Routes,
	Route,
	useLocation,
	useNavigate,
	useParams,
} from 'react-router-dom';
import {Helmet} from 'react-helmet';
import config from './common/config';
import {ThemeProvider} from './ThemeProvider';
import EditProfileView from './containers/EditProfileView';
import DiscoverView2 from './containers/discover/DiscoverView';
import CategoriesView from './containers/CategoriesView';
import SavedView from './containers/SavedView';
import PlanView from './containers/plans/PlanView';
import OrdersView from './containers/orders/OrdersView';
import SalesView from './containers/sales/SalesListView';
import SalesOrderDetailView from './containers/sales/SalesOrderDetailView';
import PlansView from './containers/plans/PlansListView';
import PlanEditView from './containers/plans/PlanEditView';
import OrderDetailViewV2 from './containers/orders/OrderDetailViewV2';
import QuickOrdersView from './containers/sales/QuickOrdersListView';
import QuickOrderDetailView from './containers/sales/QuickOrderDetailView';
import ProductsListView from './containers/products/ProductsListView';
import ProductView from './containers/products/ProductView';
import CheckoutView from './containers/CheckoutView';
import { PaymentSuccessView } from './containers/PaymentSuccessView';
import VerifyEmailNotification from './containers/VerifyEmailNotification';
import LanguageSwitcher from './components/LanguageSwitcher';
import UrlLanguageCleaner from './components/UrlLanguageCleaner';
import { useTranslation } from 'react-i18next';
import { initJ2S } from 'react-jump2source';
import ReactPixel from 'react-facebook-pixel';
import CookiePromptDialog from './containers/CookiePromptDialog';
import { trackGAPageview, trackPixelEvent, initializeGA4, updateGA4Consent } from './common/analytics';
import { CookieConsentProvider, useCookieConsent } from './contexts/CookieConsentContext';
import { ShippingRegionProvider } from './contexts/ShippingRegionContext';
import { useVersionCheck } from './hooks/useVersionCheck';
import NewVersionTooltip from './components/NewVersionTooltip';
import { SetupAccount, SetupAccountListener, SignUpHandler } from './containers/SetupAccountFlow';
import SetupAccountCreator from './containers/SetupAccountCreator';
import SetupAccountVendor from './containers/SetupAccountVendor';
import SellerProfileView from './containers/SellerProfileView';

const RedirectToShopRoute = () => {
	const { pathname } = useLocation();
	const { profileId } = useParams();
	const navigate = useNavigate();
	useEffect(() => {
		navigate('/shop/' + profileId);
	}, [pathname]);
	return null;
};

const LanguageSwitcherContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  display: flex;
  gap: 10px;
`;

// Component to track page views
const PageTracker = () => {
	const location = useLocation();
	const { cookieConsent } = useCookieConsent();
	useEffect(() => {
		if (cookieConsent === true) {
			const pagePath = location.pathname + location.search;
			trackGAPageview({ hitType: 'pageview', page: pagePath });
			trackPixelEvent('ViewContent', { contentIds: [pagePath] });
		}
	}, [location, cookieConsent]);
  
	return null;
};

function AppContent() {
	const { t } = useTranslation();
	const { cookieConsent, setCookieConsent } = useCookieConsent();

	// Version checking
	const { isNewVersionAvailable } = useVersionCheck({
		checkInterval: 5 * 60 * 1000, // Check every 5 minutes
		enabled: true,
	});

	// Initialize Tracking
	useEffect(() => {
		// Always initialize GA4 with default consent state (denied)
		if (config.gaMeasurementId) {
			initializeGA4(config.gaMeasurementId);
		}
		
		// Update consent state based on user preference
		if (cookieConsent !== null) {
			updateGA4Consent(cookieConsent);
		}
		
		// Initialize Facebook Pixel only after consent
		if (cookieConsent === true && config.metaPixelId) {
			ReactPixel.init(config.metaPixelId);
		}
	}, [cookieConsent]);

	// Only import and use react-jump2source in development
	useEffect(() => {
		if (process.env.NODE_ENV === 'development') {
			initJ2S({
				projectDir: process.env.REACT_APP_J2S_WORKSPACE_ROOT || '',
				resolver: process.env.REACT_APP_J2S_RESOLVER as 'cursor' | 'vscode'
			});
		}
	}, []);

	const handleAcceptCookieConsent = () => {
		setCookieConsent(true);
		trackGAPageview({ hitType: 'pageview', page: window.location.pathname });
	};

	return (
		<ThemeProvider>
			<CssBaseline />
			<Helmet>
				<meta charSet="utf-8" />
				<title>{t('common.appName')}</title>
				<link rel="canonical" href="https://diyfeed.com" />
			</Helmet>
			<Router>
				<PageTracker />
				<UrlLanguageCleaner />
				<LanguageSwitcherContainer>
					{isNewVersionAvailable && <NewVersionTooltip />}
					<LanguageSwitcher />
					<VerifyEmailNotification />
				</LanguageSwitcherContainer>
				<SetupAccountListener />
				<Routes>
					<Route path="/profile" element={<EditProfileView />} />
					<Route path="/" element={<DiscoverView2 />} />
					<Route path="/categories" element={<CategoriesView />} />
					<Route path="/saved" element={<SavedView />} />
					<Route path="/plans" element={<PlansView />} />
					<Route path="/plans/:id/edit" element={<PlanEditView />} />
					<Route path="/plans/:id" element={<PlanView />} />
					<Route path="/plans/:id/supplies" element={<PlanView isSuppliesTab />} />
					<Route path="/purchases" element={<OrdersView />} />
					<Route path="/purchases/:id" element={<OrderDetailViewV2 />} />
					<Route path="/sales" element={<SalesView />} />
					<Route path="/sales/:id" element={<SalesOrderDetailView />} />
					<Route path="/quick-orders" element={<QuickOrdersView />} />
					<Route path="/quick-orders/:id" element={<QuickOrderDetailView />} />
					<Route path="/checkout" element={<CheckoutView />} />
					<Route path="/build-kits" element={<QuickOrdersView />} />
					<Route path="/build-kits/:id" element={<QuickOrderDetailView />} />
					<Route path="/build-kits/checkout" element={<CheckoutView />} />
					<Route path="/build-kits/:id/checkout" element={<CheckoutView />} />
					<Route path="/products" element={<ProductsListView />} />
					<Route path="/products/:id" element={<ProductView />} />
					<Route path="/payment/success" element={<PaymentSuccessView />} />
					{/* deprecated route, remove redirect after a while */}
					<Route path="/author/:profileId" element={<RedirectToShopRoute />} />
					<Route path="/shop/:profileId" element={<SellerProfileView />} />
					<Route path="/signup/:type" element={<SignUpHandler />} />
					<Route path="/setup-account" element={<SetupAccount />} />
					<Route path="/setup-account/creator" element={<SetupAccountCreator />} />
					<Route path="/setup-account/vendor" element={<SetupAccountVendor />} />
				</Routes>
			</Router>
			<CookiePromptDialog 
				open={cookieConsent === null} 
				onAccept={handleAcceptCookieConsent} 
				onDecline={() => setCookieConsent(false)} 
			/>
		</ThemeProvider>
	);
}

function App() {
	return (
		<CookieConsentProvider>
			<ShippingRegionProvider>
				<AppContent />
			</ShippingRegionProvider>
		</CookieConsentProvider>
	);
}

export default App;
